import {
  Box,
  IconButton,
  TextField,
  Typography,
  TableFooter,
  TableRow,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { PaginationProps } from "./Table";
import { useEffect, useState } from "react";
import useDebouncedValue from "../../../hooks/useDebouncedValue";

interface Props {
  columnsCount: number;
  hasActions: boolean;
  pagination: PaginationProps;
  loading: boolean;
}

const TablePaginationFooter = ({
  columnsCount,
  hasActions,
  pagination,
  loading,
}: Props) => {
  const totalPages = Math.ceil(pagination.count / pagination.rowsPerPage);
  const currentPage = pagination.page;

  const [inputPage, setInputPage] = useState(currentPage + 1);

  useDebouncedValue(inputPage, {
    delay: 400,
    onDebounce: () => {
      const clamped = Math.max(1, Math.min(inputPage, totalPages));
      if (clamped - 1 !== currentPage) {
        pagination.onPageChange?.(null, clamped - 1);
      }
    },
  });

  useEffect(() => {
    setInputPage(currentPage + 1);
  }, [currentPage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val)) {
      setInputPage(val);
    }
  };

  const from = currentPage * pagination.rowsPerPage + 1;
  const to = Math.min(from + pagination.rowsPerPage - 1, pagination.count);

  return (
    <TableFooter>
      <TableRow>
        <td colSpan={columnsCount + (hasActions ? 1 : 0)}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1}
            gap={2}
            flexWrap="wrap"
          >
            <Typography variant="body2" color="text.secondary">
              {from}–{to} of {pagination.count}
            </Typography>

            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={() => pagination.onPageChange?.(null, 0)}
                disabled={currentPage === 0 || loading}
              >
                <FirstPageIcon />
              </IconButton>

              <IconButton
                onClick={() => pagination.onPageChange?.(null, currentPage - 1)}
                disabled={currentPage === 0 || loading}
              >
                <ChevronLeftIcon />
              </IconButton>

              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={inputPage}
                onChange={handleInputChange}
                disabled={loading}
                inputProps={{
                  min: 1,
                  max: totalPages,
                  style: {
                    textAlign: "center",
                    padding: "6px 8px",
                    width: "64px",
                  },
                }}
              />

              <Typography variant="body2" color="text.secondary">
                / {totalPages}
              </Typography>

              <IconButton
                onClick={() => pagination.onPageChange?.(null, currentPage + 1)}
                disabled={currentPage >= totalPages - 1 || loading}
              >
                <ChevronRightIcon />
              </IconButton>

              <IconButton
                onClick={() => pagination.onPageChange?.(null, totalPages - 1)}
                disabled={currentPage >= totalPages - 1 || loading}
              >
                <LastPageIcon />
              </IconButton>
            </Box>
          </Box>
        </td>
      </TableRow>
    </TableFooter>
  );
};

export default TablePaginationFooter;
